import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'recompose';

import { actions as coreActions, selectors as coreSelectors } from 'mage-core';
import { components as clientComponents } from 'mage-client';
import { MOET_CONSTANTS } from 'moet-core';

import { setupMid } from '@/actions/midSelector';
import { getSelectedMid } from '@/selectors/midSelector';
import { getMerchant } from '@/selectors/merchant';
import { ALL_CAPS, getExecCaps } from '@/caps';

const { WIDGET_TYPE } = MOET_CONSTANTS;

const ALL_CAPS_PARSED = Object.values(ALL_CAPS).reduce(
  (acc, curr) => ({ ...acc, [curr.key]: curr.cap }),
  {},
);

export const withMidChange = OriginalReactComponent => {
  const WithMidChange = ({ _withMidChangeProps, ...otherProps }) => {
    const { getMerchantRegion, merchant, mids, selectedMid } = _withMidChangeProps;
    const { basePath, history, setMid } = otherProps;

    const [currentMid, setCurrentMid] = useState(merchant.id);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
      const runExecCaps = async () => {
        const execCaps = getExecCaps(WIDGET_TYPE.UNMANAGED_KYC, false);
        const region = getMerchantRegion(selectedMid);
        const capParams = { params: { mid: selectedMid }, region };

        await Promise.all(
          Object.keys(execCaps).map(key => {
            const capability = otherProps[key] || Promise.resolve;
            return capability(capParams);
          }),
        );
      };
      const refresh = () =>
        new Promise(resolve =>
          setTimeout(() => {
            history.push(`${basePath}/`);
            return resolve();
          }, 1),
        );

      if (mids?.length && selectedMid && !loading) {
        if (mids.includes(selectedMid)) {
          /* eslint-disable promise/catch-or-return */
          if (selectedMid !== currentMid) {
            setLoading(true);
            runExecCaps().finally(() => {
              setCurrentMid(selectedMid);
              setMid(selectedMid);
              refresh().then(() => setLoading(false));
            });
          } else {
            setLoading(true);
            setCurrentMid(selectedMid);
            setMid(selectedMid);
            refresh().then(() => setLoading(false));
          }
          /* eslint-enable promise/catch-or-return */
        }
      }
    }, [currentMid, loading, selectedMid]);

    return <OriginalReactComponent {...otherProps} />;
  };

  WithMidChange.propTypes = {
    _withMidChangeProps: PropTypes.shape({
      getMerchantRegion: PropTypes.func.isRequired,
      merchant: PropTypes.object.isRequired,
      mids: PropTypes.array,
      selectedMid: PropTypes.string,
    }).isRequired,
    setMid: PropTypes.func.isRequired,
  };

  return compose(
    clientComponents.withPromiseBasedCapabilities(ALL_CAPS_PARSED),
    connect(
      state => ({
        _withMidChangeProps: {
          getMerchantRegion: mid => coreSelectors.getMerchantRegion(state)(mid),
          merchant: getMerchant(state),
          mids: coreSelectors.getMerchants(state).map(m => m.merchantId),
          selectedMid: getSelectedMid(state),
        },
      }),
      dispatch => ({
        setMid: mid => {
          dispatch(coreActions.mid.set(mid));
          dispatch(setupMid());
        },
      }),
    ),
  )(WithMidChange);
};
